import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '../../../../../environments/environment';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import { EnderecoCanalSicPublico } from '../../models/canal-sic-publico.model';
import { CanalSicPublicoService } from '../../services/canal-sic-publico.service';
import {
  CANAL_SIC_NAO_PUBLICADO,
  consultaEnderecoSic,
  enderecoSic,
  prazosSic,
  responsavelSic,
  telefoneSic,
} from '../prazos-sic';

export interface PostoSic {
  id: string;
  nome: string;
  tipo: string;
  endereco: EnderecoCanalSicPublico | null;
  bairro: string;
  telefone: string;
  email: string;
  horario: string;
  /** Nunca vazio: o nome do responsável ou "em atualização", como na página de informações. */
  responsavel: string;
}

const TODOS = '';

/** O badge do card: o canal em destaque é sempre exibido como posto de atendimento. */
const TIPO_POSTO = 'Posto de Atendimento';

@Component({
  selector: 'app-sic-presencial',
  templateUrl: './sic-presencial.component.html',
  styleUrls: ['./sic-presencial.component.css'],
  imports: [BreadcrumbComponent, FormsModule, LucideModule],
})
export class SicPresencialComponent {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly publico = inject(CanalSicPublicoService);

  /** Enquanto a leitura não termina, a tela não exibe nem o posto, nem o estado vazio, nem o mapa. */
  readonly carregando = this.publico.carregando;
  readonly naoPublicado = CANAL_SIC_NAO_PUBLICADO;

  /** Os três prazos, com o texto pronto do backend na unidade do canal. */
  readonly prazos = computed(() => prazosSic(this.publico.canal()));

  readonly trilha = [{ titulo: 'SIC', link: '/sic' }];

  /**
   * O canal em destaque como **único** posto — `[]` sem canal publicado. A maquinaria de N postos
   * (busca, filtros, contagem, *Ver no mapa*) fica dormente: com um posto, `varios()` é falso.
   */
  readonly postos = computed<readonly PostoSic[]>(() => {
    const canal = this.publico.canal();

    if (!canal) return [];

    return [
      {
        id: 'canal-em-destaque',
        nome: canal.nome ?? '',
        tipo: TIPO_POSTO,
        endereco: canal.endereco,
        bairro: canal.endereco?.bairro?.trim() ?? '',
        telefone: telefoneSic(canal.telefone),
        email: canal.email?.trim() ?? '',
        horario: canal.horarioAtendimento?.trim() ?? '',
        responsavel: responsavelSic(canal.responsavel),
      },
    ];
  });

  readonly busca = signal('');
  readonly unidadeSelecionada = signal(TODOS);
  readonly bairroSelecionado = signal(TODOS);
  readonly horarioSelecionado = signal(TODOS);

  /** Com um único posto a lista vira um card só, sem filtro — como no protótipo. */
  readonly varios = computed(() => this.postos().length > 1);

  readonly unidades = computed(() => this.opcoes((posto) => posto.nome));

  readonly bairros = computed(() => this.opcoes((posto) => posto.bairro));

  readonly horarios = computed(() => this.opcoes((posto) => posto.horario));

  readonly filtrados = computed<readonly PostoSic[]>(() => {
    const termo = this.busca().trim().toLowerCase();
    const unidade = this.unidadeSelecionada();
    const bairro = this.bairroSelecionado();
    const horario = this.horarioSelecionado();

    return this.postos().filter((posto) => {
      if (unidade !== TODOS && posto.nome !== unidade) return false;
      if (bairro !== TODOS && posto.bairro !== bairro) return false;
      if (horario !== TODOS && posto.horario !== horario) return false;
      if (!termo) return true;

      return [posto.nome, posto.tipo, posto.bairro, this.endereco(posto)]
        .join(' ')
        .toLowerCase()
        .includes(termo);
    });
  });

  private readonly selecionadoId = signal('');

  /** O posto do mapa: o escolhido no card ou, por padrão, o primeiro da lista visível. */
  readonly selecionado = computed<PostoSic | null>(() => {
    const visiveis = this.filtrados();

    return visiveis.find((posto) => posto.id === this.selecionadoId()) ?? visiveis[0] ?? null;
  });

  /** Sem posto não há mapa — nem uma localização de reserva. */
  readonly mapaUrl = computed<SafeResourceUrl | null>(() => {
    const url = this.montarUrlDoMapa();

    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });

  endereco(posto: PostoSic): string {
    return enderecoSic(posto.endereco);
  }

  selecionar(posto: PostoSic): void {
    this.selecionadoId.set(posto.id);
  }

  limparFiltros(): void {
    this.busca.set('');
    this.unidadeSelecionada.set(TODOS);
    this.bairroSelecionado.set(TODOS);
    this.horarioSelecionado.set(TODOS);
  }

  /** Abre a rota até o posto no Google Maps, em nova aba. */
  rotaUrl(posto: PostoSic): string {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(this.consulta(posto))}`;
  }

  telefoneUrl(posto: PostoSic): string {
    return `tel:${posto.telefone.replace(/\D/g, '')}`;
  }

  emailUrl(posto: PostoSic): string {
    return `mailto:${posto.email}`;
  }

  /** Valores distintos de um campo; com menos de dois o filtro correspondente não é exibido. */
  private opcoes(campo: (posto: PostoSic) => string): readonly string[] {
    return [...new Set(this.postos().map(campo))].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }

  private consulta(posto: PostoSic): string {
    return consultaEnderecoSic(posto.endereco);
  }

  /**
   * Com a chave da Embed API configurada, o modo `search` planta um marcador por posto; sem chave,
   * o mapa público mostra um posto por vez e o card escolhido define qual.
   */
  private montarUrlDoMapa(): string | null {
    const chave = environment.googleMapsKey;
    const visiveis = this.filtrados();
    const alvo = this.selecionado();

    if (!alvo) return null;

    if (chave && visiveis.length > 1) {
      const termo = visiveis.map((posto) => this.consulta(posto)).join(' | ');

      return `https://www.google.com/maps/embed/v1/search?key=${chave}&q=${encodeURIComponent(termo)}&zoom=12`;
    }

    if (chave) {
      return `https://www.google.com/maps/embed/v1/place?key=${chave}&q=${encodeURIComponent(
        this.consulta(alvo)
      )}&zoom=16`;
    }

    return `https://maps.google.com/maps?q=${encodeURIComponent(this.consulta(alvo))}&z=16&output=embed`;
  }
}
